// src/features/simulators/online-checkin/hooks/useOnlineCheckInSimulation.ts

import { useCallback } from 'react';
import type { AnalyticsClient } from '../../shared/utils/analyticsClient';
import type { LogEntry } from '../../shared/types/simulator.types';
import type { SimulationSummary } from '../../shared/hooks/useSimulatorWizard';
import type { OnlineCheckInConfigValues } from '../components/OnlineCheckInConfigForm';
import {
  JourneyEventCode,
  SectionCode,
  GuestType,
  KeyDeliveryMethod,
  createCorrelatedEvent,
  type JourneyState,
} from '../../shared/types/journey.types';
import {
  generateAlternateReservationNumbers,
  generateRoomNumber,
  generatePaymentData,
  generateSectionDuration,
  createJourneyState,
} from '../../shared/utils/journeyDataGenerator';
import { LinkAccessMethod, ONLINE_CHECKIN_CHANNEL } from '../types';

type LogFn = (type: LogEntry['type'], message: string, details?: any) => void;

const ERROR_RATE = 0.05;
const ABANDONMENT_RATE = 0.10;
const DIGITAL_KEY_RATE = 0.20;

/**
 * Helper: traverse a section (SECTION_ENTER + delay + SECTION_EXIT)
 */
const traverseSection = async (
  client: AnalyticsClient,
  addLog: LogFn,
  deviceId: string,
  hotelId: string,
  journey: JourneyState,
  sectionCode: SectionCode,
  sectionLabel: string,
  extraEnterMeta?: Record<string, string>,
  extraExitMeta?: Record<string, string>,
): Promise<number> => {
  const duration = generateSectionDuration(sectionCode);
  addLog('info', `[Online] Section ${sectionCode} - ${sectionLabel} (${duration}s)`);

  await client.sendEvent(
    createCorrelatedEvent(
      JourneyEventCode.SECTION_ENTER,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode,
        ...extraEnterMeta,
      },
    ),
  );
  journey.currentSection = sectionCode;
  await new Promise(resolve => setTimeout(resolve, duration * 100));

  await client.sendEvent(
    createCorrelatedEvent(
      JourneyEventCode.SECTION_EXIT,
      deviceId,
      hotelId,
      journey,
      {
        sectionCode,
        duration: duration.toString(),
        ...extraExitMeta,
      },
    ),
  );
  journey.completedSections.push(sectionCode);
  journey.currentSection = undefined;

  // 2 events per section (enter + exit)
  return 2;
};

export const useOnlineCheckInSimulation = () => {
  const simulate = useCallback(async (
    client: AnalyticsClient,
    addLog: LogFn,
    deviceId: string,
    hotelId: string,
    config: OnlineCheckInConfigValues,
  ): Promise<SimulationSummary> => {
    const startTime = Date.now();
    let totalEvents = 0;
    let successful = 0;
    let failed = 0;
    let abandoned = 0;

    // Determine reservation number
    const reservationToUse = (!config.autoGenerateReservation && config.reservationNumber)
      ? config.reservationNumber
      : undefined;

    // Create journey state
    const journey = createJourneyState({
      guestType: GuestType.RESERVATION,
      hasPreCheckIn: false,
      reservationNumber: reservationToUse,
    });

    // Possibly add alternate reservation numbers
    if (Math.random() > 0.7) {
      journey.alternateReservationNumbers = generateAlternateReservationNumbers(journey.reservationNumber!);
      addLog('info', `Multiple reservations: ${journey.reservationNumber} + ${journey.alternateReservationNumbers.join(', ')}`);
    }

    // Assign room number
    journey.roomNumber = generateRoomNumber();

    addLog('info', '========================================');
    addLog('info', `Guest accesses pre-check-in link (${config.linkAccessMethod})`);
    addLog('info', `Reservation: ${journey.reservationNumber}`);
    if (journey.alternateReservationNumbers && journey.alternateReservationNumbers.length > 0) {
      addLog('info', `Alternate: ${journey.alternateReservationNumbers.join(', ')}`);
    }
    addLog('info', '========================================');

    // --- Check for abandonment ---
    if (Math.random() < ABANDONMENT_RATE) {
      const abandonSections = [SectionCode.S, SectionCode.I, SectionCode.F, SectionCode.U, SectionCode.P];
      const abandonedAt = abandonSections[Math.floor(Math.random() * abandonSections.length)];

      addLog('warning', `[Online] Guest abandoned at section ${abandonedAt}`);

      await client.sendEvent(
        createCorrelatedEvent(
          JourneyEventCode.AB,
          deviceId,
          hotelId,
          journey,
          {
            sectionCode: abandonedAt,
            abandonReason: 'user_exit',
            duration: Math.floor((Date.now() - startTime) / 1000).toString(),
            linkAccessMethod: config.linkAccessMethod,
          },
        ),
      );
      totalEvents++;
      abandoned++;

      return {
        success: false,
        totalEvents,
        successful,
        failed,
        abandoned,
        duration: Date.now() - startTime,
        error: 'Abandoned',
      };
    }

    // --- PC_START ---
    addLog('info', '[Online] Guest starts pre-check-in');
    await client.sendEvent(
      createCorrelatedEvent(
        JourneyEventCode.PC_START,
        deviceId,
        hotelId,
        journey,
        {
          linkAccessMethod: config.linkAccessMethod,
        },
      ),
    );
    journey.preCheckInStarted = true;
    totalEvents++;
    await new Promise(resolve => setTimeout(resolve, 500));

    // --- Section S - SEARCH ---
    totalEvents += await traverseSection(
      client, addLog, deviceId, hotelId, journey,
      SectionCode.S, 'Search Reservation',
      { action: 'searchReservation' },
    );
    await new Promise(resolve => setTimeout(resolve, 300));

    // --- Section I - IDENTIFICATION ---
    totalEvents += await traverseSection(
      client, addLog, deviceId, hotelId, journey,
      SectionCode.I, 'Guest Identification',
      { action: 'guestIdentification' },
      { idType: 'passport' },
    );
    await new Promise(resolve => setTimeout(resolve, 300));

    // --- Section F - SIGNATURE ---
    totalEvents += await traverseSection(
      client, addLog, deviceId, hotelId, journey,
      SectionCode.F, 'Digital Signature',
      { action: 'digitalSignature' },
      { signatureType: 'digital' },
    );
    await new Promise(resolve => setTimeout(resolve, 300));

    // --- Section U - UPSELLING ---
    const acceptedUpsell = Math.random() > 0.5;
    addLog('info', `[Online] Upsell ${acceptedUpsell ? 'Accepted' : 'Declined'}`);
    totalEvents += await traverseSection(
      client, addLog, deviceId, hotelId, journey,
      SectionCode.U, 'Upselling',
      { action: 'upsellOffers' },
      { offersShown: '3', offersAccepted: acceptedUpsell ? '1' : '0' },
    );
    await new Promise(resolve => setTimeout(resolve, 300));

    // --- Section P - PAYMENT ---
    const paymentFailed = Math.random() < ERROR_RATE;
    const payment = generatePaymentData();
    const paymentDuration = generateSectionDuration(SectionCode.P);

    addLog('info', `[Online] Section P - Payment (${paymentDuration}s)`);

    await client.sendEvent(
      createCorrelatedEvent(
        JourneyEventCode.SECTION_ENTER,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.P,
          action: 'processPayment',
        },
      ),
    );
    journey.currentSection = SectionCode.P;
    totalEvents++;
    await new Promise(resolve => setTimeout(resolve, paymentDuration * 100));

    if (paymentFailed) {
      addLog('error', `[Online] Payment FAILED (${payment.amount} EUR)`);

      await client.sendEvent(
        createCorrelatedEvent(
          JourneyEventCode.PM_ERROR,
          deviceId,
          hotelId,
          journey,
          {
            sectionCode: SectionCode.P,
            errorCode: 'CARD_DECLINED',
            amount: payment.amount.toString(),
            paymentMethod: payment.method,
          },
        ),
      );
      totalEvents++;
      failed++;

      return {
        success: false,
        totalEvents,
        successful,
        failed,
        abandoned,
        duration: Date.now() - startTime,
        error: 'Payment failed',
      };
    }

    // Payment successful
    addLog('success', `[Online] Payment SUCCESSFUL (${payment.amount} EUR)`);
    await client.sendEvent(
      createCorrelatedEvent(
        JourneyEventCode.PM_SUCCESS,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.P,
          amount: payment.amount.toString(),
          paymentMethod: payment.method,
          operationId: payment.operationId,
        },
      ),
    );
    totalEvents++;

    await client.sendEvent(
      createCorrelatedEvent(
        JourneyEventCode.SECTION_EXIT,
        deviceId,
        hotelId,
        journey,
        {
          sectionCode: SectionCode.P,
          duration: paymentDuration.toString(),
        },
      ),
    );
    journey.completedSections.push(SectionCode.P);
    journey.currentSection = undefined;
    totalEvents++;
    await new Promise(resolve => setTimeout(resolve, 300));

    // --- PC_COMP ---
    addLog('success', '[Online] PRE-CHECK-IN COMPLETED');

    const isDigitalKey = [
      KeyDeliveryMethod.DIGITAL_PIN,
      KeyDeliveryMethod.DIGITAL_MOBILE,
    ].includes(config.keyDeliveryMethod);

    const willDeliverDigitalKey = isDigitalKey && Math.random() < DIGITAL_KEY_RATE;

    const pcCompEvent = createCorrelatedEvent(
      JourneyEventCode.PC_COMP,
      deviceId,
      hotelId,
      journey,
      {
        totalDuration: Math.floor((Date.now() - startTime) / 1000).toString(),
        keyDeliveryMethod: config.keyDeliveryMethod,
        willDeliverDigitalKey: willDeliverDigitalKey.toString(),
        pmsRegistered: 'true',
      },
    );
    pcCompEvent.roomNumber = journey.roomNumber;

    await client.sendEvent(pcCompEvent);
    journey.preCheckInCompleted = true;
    totalEvents++;

    await client.updateJourney({
      reservationNumber: journey.reservationNumber!,
      alternateReservationNumbers: journey.alternateReservationNumbers,
      hotelId,
      channel: ONLINE_CHECKIN_CHANNEL,
      eventType: 'PreCheckInCompleted',
      timestamp: new Date().toISOString(),
    });

    // --- Digital key delivery (if applicable) ---
    let digitalKeyDelivered = false;
    if (willDeliverDigitalKey) {
      await new Promise(resolve => setTimeout(resolve, 500));

      // 2% failure rate for digital key delivery
      if (Math.random() < 0.02) {
        addLog('error', '[Digital Key] Delivery failed');

        const kdErrorEvent = createCorrelatedEvent(
          JourneyEventCode.KD_ERROR,
          deviceId,
          hotelId,
          journey,
          {
            sectionCode: SectionCode.K,
            errorCode: 'KEY_DELIVERY_FAILED',
            deliveryMethod: config.keyDeliveryMethod,
          },
        );
        kdErrorEvent.roomNumber = journey.roomNumber;
        await client.sendEvent(kdErrorEvent);
        totalEvents++;
      } else {
        addLog('success', `[Digital Key] Delivered via ${config.keyDeliveryMethod} for room ${journey.roomNumber}`);

        const kdSuccessEvent = createCorrelatedEvent(
          JourneyEventCode.KD_SUCCESS,
          deviceId,
          hotelId,
          journey,
          {
            sectionCode: SectionCode.K,
            deliveryMethod: config.keyDeliveryMethod,
          },
        );
        kdSuccessEvent.roomNumber = journey.roomNumber;
        await client.sendEvent(kdSuccessEvent);
        totalEvents++;
        digitalKeyDelivered = true;
        journey.keyDispensed = true;
      }
    }

    successful++;

    const duration = Date.now() - startTime;
    addLog('success', '========================================');
    addLog('success', `Pre-check-in COMPLETE (${(duration / 1000).toFixed(1)}s)`);
    addLog('success', `Room: ${journey.roomNumber}`);
    if (digitalKeyDelivered) {
      addLog('success', `Digital key delivered via ${config.keyDeliveryMethod}`);
    }
    addLog('success', '========================================');

    return {
      success: true,
      totalEvents,
      successful,
      failed,
      abandoned,
      duration,
    };
  }, []);

  return { simulate };
};
